import { type SceneKeyframe, easeOutProgress, phaseAt } from '../scene-clock'

/**
 * Timeline and demo data for the landlord "Lease And LMR In Minutes" scene
 * (the reference's #lsvis): a lease template gets its clauses switched on,
 * the approved application fills in the tenant's details, the lease is
 * sent, both sides sign, and last month's rent counts up to collected.
 * Everything here is pure and unit tested; LeaseLmrScene only draws a
 * phase and a progress value.
 *
 * Milliseconds are the reference's (one 10 s loop):
 *   500 / 1000 / 1500  the three clauses switch on
 *   2300               status "Tenant approved"
 *   2600 + i * 380     the three fields fill in
 *   4000               status "Sent"
 *   4600               the tenant signs
 *   5300               the landlord signs, status "Signed", LMR "Collecting"
 *   5700 to 7200       the amount counts $0 to $3,200 (cubic ease-out) while
 *                      the bar fills, then the LMR pill becomes "Received"
 */

export const LEASE_LOOP_MS = 10_000

export const LEASE_PHASES = [
	'template',
	'clause1',
	'clause2',
	'clause3',
	'approved',
	'field1',
	'field2',
	'field3',
	'sent',
	'tenantSigned',
	'signed',
	'collecting',
	'received',
] as const

export type LeasePhase = (typeof LEASE_PHASES)[number]

export const LEASE_FINAL_PHASE: LeasePhase = 'received'

/** The count-up: starts when both have signed and lands as the pill flips. */
export const LMR_COUNT = {
	startMs: 5700,
	durationMs: 1500,
	amount: 3200,
} as const

export const LEASE_KEYFRAMES: readonly SceneKeyframe<LeasePhase>[] = [
	{ at: 0, phase: 'template' },
	{ at: 500, phase: 'clause1' },
	{ at: 1000, phase: 'clause2' },
	{ at: 1500, phase: 'clause3' },
	{ at: 2300, phase: 'approved' },
	{ at: 2600, phase: 'field1' },
	{ at: 2980, phase: 'field2' },
	{ at: 3360, phase: 'field3' },
	{ at: 4000, phase: 'sent' },
	{ at: 4600, phase: 'tenantSigned' },
	{ at: 5300, phase: 'signed' },
	{ at: LMR_COUNT.startMs, phase: 'collecting' },
	{ at: LMR_COUNT.startMs + LMR_COUNT.durationMs, phase: 'received' },
]

export function leasePhaseAt(elapsedMs: number): LeasePhase {
	return phaseAt(elapsedMs, LEASE_KEYFRAMES, LEASE_LOOP_MS)
}

export function leasePhaseIndex(phase: LeasePhase): number {
	return LEASE_PHASES.indexOf(phase)
}

/** True once the scene has reached `target` (phases are cumulative). */
export function leaseReached(phase: LeasePhase, target: LeasePhase): boolean {
	return leasePhaseIndex(phase) >= leasePhaseIndex(target)
}

/** Milliseconds into the current loop, for any elapsed time. */
function loopTime(elapsedMs: number): number {
	return ((elapsedMs % LEASE_LOOP_MS) + LEASE_LOOP_MS) % LEASE_LOOP_MS
}

/**
 * How far the count-up has got, 0..1 with a cubic ease-out: 0 until the
 * landlord has signed, 1 from the moment the pill says "Received".
 */
export function lmrProgressAt(elapsedMs: number): number {
	return easeOutProgress(
		loopTime(elapsedMs),
		LMR_COUNT.startMs,
		LMR_COUNT.startMs + LMR_COUNT.durationMs,
	)
}

/** The dollars on the LMR card `elapsedMs` into the (looping) scene. */
export function lmrAmountAt(elapsedMs: number): number {
	return Math.round(LMR_COUNT.amount * lmrProgressAt(elapsedMs))
}

const dollars = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 })

/** "$3,200": whole Canadian dollars with the en-CA thousands separator. */
export function formatLmrAmount(value: number): string {
	return `$${dollars.format(Math.max(0, Math.round(value)))}`
}

export interface LeasePill {
	label: string
	green: boolean
}

export interface LeaseSceneState {
	/** How many of the three clause switches are on, top to bottom. */
	clausesOn: number
	/** How many of the three application fields have filled in. */
	fieldsOn: number
	/** The pill in the lease header. */
	status: LeasePill
	tenantSigned: boolean
	landlordSigned: boolean
	/** The pill on the LMR card. */
	lmr: LeasePill
}

/* ---------- demo data (the reference's, verbatim) ---------- */

export const LEASE_SCENE = {
	eyebrow: 'Your Lease',
	address: '214 Queen St W',
	status: {
		template: 'Template',
		approved: 'Tenant approved',
		sent: 'Sent',
		signed: 'Signed',
	},
	clausesLabel: 'Your Clauses',
	clauses: ['Rent discounts', 'Rent deposit', 'Tenant insurance'],
	fieldsLabel: 'From The Approved Application',
	fields: [
		{ label: 'Tenant', value: 'Sarah Johnson' },
		{ label: 'Monthly rent', value: formatLmrAmount(LMR_COUNT.amount) },
		{ label: 'Start date', value: 'Nov 1, 2026' },
	],
	signatures: [
		{ label: 'Tenant', name: 'S. Johnson' },
		{ label: 'Landlord', name: 'K. Carlsen' },
	],
	lmr: {
		eyebrow: 'Last Month’s Rent',
		waiting: 'Waiting for signature',
		collecting: 'Collecting',
		received: 'Received',
	},
} as const

/** Everything the component needs to draw one phase. */
export function leaseStateAt(phase: LeasePhase): LeaseSceneState {
	const reached = (target: LeasePhase) => leaseReached(phase, target)
	const { status, lmr } = LEASE_SCENE
	const clausesOn = reached('clause3')
		? 3
		: reached('clause2')
			? 2
			: reached('clause1')
				? 1
				: 0
	const fieldsOn = reached('field3')
		? 3
		: reached('field2')
			? 2
			: reached('field1')
				? 1
				: 0
	return {
		clausesOn,
		fieldsOn,
		status: reached('signed')
			? { label: status.signed, green: true }
			: reached('sent')
				? { label: status.sent, green: false }
				: reached('approved')
					? { label: status.approved, green: true }
					: { label: status.template, green: false },
		tenantSigned: reached('tenantSigned'),
		landlordSigned: reached('signed'),
		lmr: reached('received')
			? { label: lmr.received, green: true }
			: reached('signed')
				? { label: lmr.collecting, green: false }
				: { label: lmr.waiting, green: false },
	}
}
